import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Calendar, User, ArrowLeft } from "lucide-react";
import SEOHead from "@/components/SEOHead";

export default function BlogDetail() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/blogs/${slug}`).then((r) => setBlog(r.data)).catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-32 text-center">
        <h1 className="font-poppins text-3xl text-slate-900">Article not found</h1>
        <Link to="/blog" className="text-green-700 mt-4 inline-block">← Back to blog</Link>
      </div>
    );
  }
  if (!blog) {
    return <div className="text-center py-32 text-slate-500">Loading...</div>;
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: blog.title,
    image: blog.cover_image,
    author: { "@type": "Person", name: blog.author },
    datePublished: blog.created_at,
    dateModified: blog.updated_at || blog.created_at,
    publisher: {
      "@type": "Organization",
      name: "Dr. Ashutosh Mishra Clinic",
    },
  };

  return (
    <div data-testid={`blog-detail-${blog.slug}`}>
      <SEOHead
        title={`${blog.title} | Dr. Ashutosh Mishra`}
        description={blog.excerpt}
        path={`/blog/${blog.slug}`}
        image={blog.cover_image}
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Blog", url: "/blog" },
          { name: blog.title, url: `/blog/${blog.slug}` },
        ]}
        schemas={[{ id: "ld-article", data: articleSchema }]}
      />
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <Link to="/blog" className="text-sm text-green-700 inline-flex items-center gap-1 hover:gap-2 transition-all">
          <ArrowLeft className="w-4 h-4" /> All articles
        </Link>
        <div className="mt-6 text-xs uppercase tracking-wider text-green-700 font-medium">{blog.category}</div>
        <h1 className="font-poppins font-semibold text-4xl sm:text-5xl text-slate-900 mt-3 leading-tight">{blog.title}</h1>
        <div className="flex flex-wrap items-center gap-5 mt-5 text-sm text-slate-500">
          <span className="inline-flex items-center gap-1.5"><User className="w-4 h-4" />{blog.author}</span>
          <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4" />{new Date(blog.created_at).toLocaleDateString()}</span>
          <span>{blog.read_time}</span>
        </div>
        {blog.cover_image && (
          <img src={blog.cover_image} alt={blog.title} className="w-full h-[420px] object-cover rounded-3xl mt-8" />
        )}
        <p className="mt-8 text-lg text-slate-700 leading-relaxed font-medium border-l-4 border-green-600 pl-5 italic">
          {blog.excerpt}
        </p>
        <div className="blog-content mt-10" dangerouslySetInnerHTML={{ __html: blog.content }} />
      </article>

      <section className="bg-slate-950 text-white py-14">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-poppins font-semibold text-3xl">Have questions about your health?</h2>
          <p className="text-slate-300 mt-3">Book a consultation with Dr. Ashutosh Mishra and get a personalised plan.</p>
          <Link to="/contact" className="inline-block mt-7 bg-green-500 hover:bg-green-400 text-slate-900 rounded-full px-7 py-3.5 font-medium text-sm">
            Book Appointment
          </Link>
        </div>
      </section>
    </div>
  );
}
